import { useEffect, useRef } from './wp';
import { SocialIcon } from './icons';

/**
 * Gravity Form 70 is printed by PHP into a hidden stash so Gravity Forms can
 * bind its own scripts to it. We adopt that node rather than re-rendering the
 * form, which would strip those bindings (and reCAPTCHA v3 with them).
 */
function SubscribeForm() {
    const host = useRef(null);

    useEffect(() => {
        const stash = document.getElementById('tsa-chrome-gform');
        if (!stash || !host.current || host.current.contains(stash)) {
            return;
        }

        stash.removeAttribute('aria-hidden');
        stash.classList.remove('tsa-chrome-gform-stash');
        host.current.appendChild(stash);
    }, []);

    return <div className="tsa-ftr__formhost" ref={host} />;
}

function WhyChoose({ why }) {
    return (
        <section className="tsa-ftr__why">
            <h2>
                {why.heading.split('\n').map((line, index) => (
                    <span key={line} style={{ display: 'block' }}>
                        {line}
                    </span>
                ))}
            </h2>
            <div className="tsa-ftr__whygrid">
                {why.items.map((item) => (
                    <article className="tsa-ftr__whyitem" key={item.title}>
                        <img src={item.img} alt="" loading="lazy" style={{ objectFit: item.fit }} />
                        <h3>{item.title}</h3>
                        <p>{item.text}</p>
                    </article>
                ))}
            </div>
        </section>
    );
}

function LinkColumn({ column }) {
    return (
        <div className="tsa-ftr__col">
            <h3 className="tsa-ftr__colhead">
                {column.headingUrl ? <a href={column.headingUrl}>{column.heading}</a> : column.heading}
            </h3>
            <ul>
                {column.links.map((link) => (
                    <li key={link.url + link.label}>
                        <a href={link.url}>{link.label}</a>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function Footer({ config }) {
    const f = config.footer;

    return (
        <div className="tsa-ftr">
            <WhyChoose why={f.why} />

            <section className="tsa-ftr__subscribe">
                <div className="tsa-ftr__subscribe-copy">
                    <h2>{f.subscribe.heading}</h2>
                    <p>{f.subscribe.text}</p>
                </div>
                <SubscribeForm />
            </section>

            <section className="tsa-ftr__main">
                <div className="tsa-ftr__inner">
                    <div className="tsa-ftr__brand">
                        <img src={f.brand.logo} alt={config.logo.alt} width="200" loading="lazy" />
                        <p>{f.brand.text}</p>
                    </div>

                    {/* Order matches the block footer: link column, contact, link column. */}
                    <div className="tsa-ftr__cols">
                        {f.columns.slice(0, 1).map((column) => (
                            <LinkColumn column={column} key={column.heading} />
                        ))}

                        <div className="tsa-ftr__col">
                            <h3 className="tsa-ftr__colhead">{f.contact.heading}</h3>
                            <p>
                                <strong>Email:</strong>
                                <br />
                                <a href={`mailto:${f.contact.email}`}>{f.contact.email}</a>
                            </p>
                            <p>
                                <strong>Office Hours:</strong>
                                {f.contact.hours.map((line) => (
                                    <span key={line}>
                                        <br />
                                        {line}
                                    </span>
                                ))}
                            </p>
                            <p>
                                <strong>Follow Us:</strong>
                            </p>
                            <div className="tsa-ftr__social">
                                {f.contact.social.map((item) => (
                                    <a key={item.service} href={item.url} target="_blank" rel="noopener" aria-label={item.service}>
                                        <SocialIcon service={item.service} />
                                    </a>
                                ))}
                            </div>
                        </div>

                        {f.columns.slice(1).map((column) => (
                            <LinkColumn column={column} key={column.heading} />
                        ))}
                    </div>
                </div>

                <div className="tsa-ftr__bottom">
                    <p>{f.copyright}</p>
                    <p className="tsa-ftr__legal">
                        {f.legal.map((link, index) => (
                            <span key={link.url}>
                                {index > 0 && <span aria-hidden="true"> | </span>}
                                <a href={link.url}>{link.label}</a>
                            </span>
                        ))}
                    </p>
                </div>
            </section>
        </div>
    );
}
